import { useEffect } from "react";
import { CONCEPTS, Lockup, WordSVG } from "@/brand/marks";
import { BrandRefinement } from "@/brand/BrandApplications";

const INK = "#111111";
const PAPER = "#FFFFFF";

const Panel = ({ dark, children, className = "" }) => (
  <div
    className={`p-8 lg:p-10 ${className}`}
    style={{ background: dark ? INK : PAPER, color: dark ? PAPER : INK, border: dark ? "none" : "1px solid #E4E4E4" }}
  >
    {children}
  </div>
);

const Label = ({ children }) => (
  <p className="font-mono-ss text-[10px] tracking-[0.22em] uppercase opacity-50 mb-4">{children}</p>
);

const SymbolBox = ({ Symbol, viewBox, size, bg, label }) => (
  <div className="flex flex-col items-center gap-2">
    <div className="flex items-center justify-center" style={{ width: size, height: size }}>
      <svg viewBox={viewBox} width={size} height={size} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
        <Symbol bg={bg} />
      </svg>
    </div>
    <span className="font-mono-ss text-[9px] opacity-40">{label}</span>
  </div>
);

const ConceptRow = ({ c, index, dark }) => {
  const bg = dark ? INK : PAPER;
  return (
    <Panel dark={dark}>
      <div className="grid grid-cols-12 gap-8 items-center">
        <div className="col-span-12 lg:col-span-5">
          <Label>Primary wordmark</Label>
          <WordSVG tracking={c.wordTracking} renderS={c.renderS} style={{ width: "100%", height: "auto" }} />
        </div>
        <div className="col-span-12 lg:col-span-2 flex justify-center">
          <div>
            <Label>Symbol</Label>
            <SymbolBox Symbol={c.Symbol} viewBox={c.symbolViewBox} size={120} bg={bg} label="symbol" />
          </div>
        </div>
        <div className="col-span-12 lg:col-span-5">
          <Label>Wordmark + symbol</Label>
          <Lockup Symbol={c.Symbol} symbolViewBox={c.symbolViewBox} lockupScale={c.lockupScale} renderS={c.renderS} bg={bg} style={{ width: "100%", height: "auto" }} />
          <div className="mt-6 flex items-end gap-6">
            <Label>Recognition test</Label>
            {[64, 32, 24, 16].map((s) => (
              <SymbolBox key={s} Symbol={c.Symbol} viewBox={c.symbolViewBox} size={s} bg={bg} label={`${s}px`} />
            ))}
          </div>
        </div>
      </div>
    </Panel>
  );
};

const CRITERIA = ["Distinctive", "Timeless", "Architectural", "Editorial", "Memorable", "Scalable", "Place (no cliché)", "Digital", "Premium"];
const SCORES = {
  horizon:    [5, 5, 5, 5, 4, 5, 4, 5, 5],
  reflection: [3, 4, 3, 4, 3, 2, 3, 3, 4],
  threshold:  [4, 4, 4, 4, 3, 3, 4, 3, 4],
  meander:    [4, 3, 4, 3, 4, 5, 3, 5, 3],
  parcel:     [3, 5, 5, 4, 3, 4, 4, 4, 4],
  section:    [5, 5, 4, 5, 5, 4, 4, 4, 5],
};
const NOTES = {
  horizon: "Strongest idea-to-form ratio. One cut carries horizon, division, precision. Works at 16px. Risk: the shift must stay restrained or it reads as a glitch.",
  reflection: "Elegant but literal; at small sizes the pair collapses into a blur and the lighter half disappears on dark. Depends on tone, fails monochrome test.",
  threshold: "Honest and architectural, but the tall stepped stack is awkward in square formats; the rule becomes invisible below 32px.",
  meander: "Scalable and bold, but the continuous double-S resolves into a currency sign (a dollar / chain reading) — a fatal association for an advisory brand. The geometric monoline also sits apart from the serif wordmark. Rejected.",
  parcel: "Credible and quiet; the frame is a familiar device (frames are common) so ownership is weaker. The open edge is subtle to the point of being missed.",
  section: "Deeply apt (law, index, reference, doubled initial) and ownable. Needs interlock clarity at 16px — knockouts must be tuned. Could read as '§' to lawyers, which is a feature.",
};

export default function BrandReview() {
  useEffect(() => {
    document.title = "Brand identity review — Selling Sunsets";
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  return (
    <main className="min-h-screen" style={{ background: "#F4F4F2", color: INK }} data-testid="brand-review">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-12 py-16">
        <header className="mb-16 max-w-3xl">
          <p className="font-mono-ss text-[11px] tracking-[0.22em] uppercase opacity-50">Selling Sunsets · Identity programme · Stage 01</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light mt-4 leading-[1.02]">Six directions, black on white.</h1>
          <p className="mt-6 text-[0.95rem] leading-relaxed opacity-70">
            Lettering is outlined from Cormorant Garamond and re-set with proprietary spacing; no web font is used in any mark.
            Every symbol is shown at 64, 32, 24 and 16 pixels. Colour is withheld until the form is resolved.
          </p>
        </header>

        <div className="space-y-20">
          {CONCEPTS.map((c, i) => (
            <section key={c.key} data-testid={`concept-${c.key}`}>
              <div className="flex items-baseline gap-6 mb-6">
                <span className="font-mono-ss text-[11px] tracking-[0.22em] opacity-50">0{i + 1}</span>
                <h2 className="font-display text-3xl font-light">{c.name}</h2>
                <p className="text-[0.9rem] opacity-70 max-w-2xl">{c.idea}</p>
              </div>
              <div className="grid grid-cols-1 gap-px">
                <ConceptRow c={c} index={i} dark={false} />
                <ConceptRow c={c} index={i} dark />
              </div>
            </section>
          ))}
        </div>

        <section className="mt-28" data-testid="evaluation">
          <p className="font-mono-ss text-[11px] tracking-[0.22em] uppercase opacity-50">Stage 02 · Critical evaluation</p>
          <h2 className="font-display text-4xl font-light mt-3 mb-10">Scored against nine criteria</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-[0.82rem]" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr className="font-mono-ss text-[10px] tracking-[0.16em] uppercase opacity-60">
                  <th className="text-left py-3 pr-6 border-b border-black/20">Direction</th>
                  {CRITERIA.map((k) => (
                    <th key={k} className="text-center py-3 px-2 border-b border-black/20 font-normal">{k}</th>
                  ))}
                  <th className="text-center py-3 px-2 border-b border-black/20">Total</th>
                </tr>
              </thead>
              <tbody>
                {CONCEPTS.map((c) => {
                  const s = SCORES[c.key];
                  const total = s.reduce((a, b) => a + b, 0);
                  const top = total >= 36;
                  return (
                    <tr key={c.key} className={top ? "font-medium" : "opacity-60"}>
                      <td className="py-3 pr-6 border-b border-black/10 font-display text-lg">{c.name}{top && <span className="font-mono-ss text-[9px] ml-3 tracking-[0.2em] opacity-60">SHORTLIST</span>}</td>
                      {s.map((v, i) => (
                        <td key={i} className="text-center py-3 px-2 border-b border-black/10 font-mono-ss">{v}</td>
                      ))}
                      <td className="text-center py-3 px-2 border-b border-black/10 font-mono-ss">{total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-x-12 gap-y-6 max-w-5xl">
            {CONCEPTS.map((c) => (
              <div key={c.key}>
                <p className="font-display text-xl">{c.name}</p>
                <p className="text-[0.85rem] opacity-70 leading-relaxed mt-1">{NOTES[c.key]}</p>
              </div>
            ))}
          </div>
        </section>

        <BrandRefinement />
      </div>
    </main>
  );
}
